import React, { useState, useRef } from 'react';
import {
  ProTableOt, useProTableOt, Button, Badge, Modal, Form, Input, Select,
  Row, Col, LucideIcon, message, useCRUDController
} from './ot.jsx';

const DEFAULT_STATS = [
  { value: 0, label: "總筆數",   icon: "Database",    bgColor: "bg-blue-50",  iconColor: "text-blue-500"  },
  { value: 0, label: "本月新增", icon: "Plus",        bgColor: "bg-green-50", iconColor: "text-green-500" },
  { value: 0, label: "啟用中",   icon: "CheckCircle", bgColor: "bg-teal-50",  iconColor: "text-teal-500"  },
  { value: 0, label: "已停用",   icon: "XCircle",     bgColor: "bg-gray-50",  iconColor: "text-gray-400"  },
];

const StatsRow = ({ stats }) => (
  <Row gutter={[16, 16]} style={{ padding: '16px 16px 0' }}>
    {stats.map((s, i) => (
      <Col span={6} key={i}>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.bgColor}`}>
            <LucideIcon name={s.icon} className={`w-5 h-5 ${s.iconColor}`} />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

const CATEGORY_OPTIONS = [
  { value: '業務', label: '業務' },
  { value: '人資', label: '人資' },
  { value: '技術', label: '技術' },
  { value: '行政', label: '行政' },
];

const STATUS_OPTIONS = [
  { value: 'active',   label: '啟用' },
  { value: 'inactive', label: '停用' },
];

const columns = [
  { title: '名稱',     dataIndex: 'name',             key: 'name',             width: 180 },
  { title: '分類',     dataIndex: 'category',         key: 'category',         width: 100 },
  {
    title: '狀態', dataIndex: 'status', key: 'status', width: 90,
    render: (val) => (
      <Badge color={val === 'active' ? 'green' : 'gray'} text={val === 'active' ? '啟用' : '停用'} />
    ),
  },
  { title: '建立人員', dataIndex: 'create_user_name', key: 'create_user_name', width: 110 },
  { title: '建立時間', dataIndex: 'create_time',      key: 'create_time',      width: 130 },
];

const Name12 = ({
  mainApiRoute = '/utility/data',
  request = null,
  stats = null,
  propsCategoryName = '資料',
  propsCategoryTitle = '管理',
}) => {
  const actionRef = useRef();
  const [form] = Form.useForm();
  const { check, setCheck } = useProTableOt({ columns });

  const [modalOpen,    setModalOpen]    = useState(false);
  const [editingRow,   setEditingRow]   = useState(null);
  const [saving,       setSaving]       = useState(false);

  const displayStats = stats || DEFAULT_STATS;

  const { handleCreate, handleUpdate, handleDelete } = useCRUDController({ mainApiRoute, actionRef });

  const openNew = () => {
    setEditingRow(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    form.setFieldsValue({
      name:     row.name,
      category: row.category,
      status:   row.status,
      remark:   row.remark || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingRow) {
        handleUpdate && await handleUpdate({ ...editingRow, ...values });
      } else {
        handleCreate && await handleCreate(values);
      }
      message.success(editingRow ? '更新成功' : '新增成功');
      setModalOpen(false);
      actionRef.current?.reload?.();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('操作失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (row) => {
    try {
      handleDelete && await handleDelete(row.id);
      message.success('刪除成功');
      actionRef.current?.reload?.();
    } catch {
      message.error('刪除失敗');
    }
  };

  const handleRequest = async (params) => {
    if (request) return request(params);
    return { data: [], total: 0, success: true };
  };

  const tableColumns = [
    ...columns,
    {
      title: '操作', key: 'action', width: 130, fixed: 'right',
      render: (_, row) => (
        <div className="flex gap-2">
          <Button size="small" onClick={() => openEdit(row)}>
            <LucideIcon name="Pencil" className="w-3.5 h-3.5 mr-1 inline" />編輯
          </Button>
          <Button size="small" danger onClick={() => handleRemove(row)}>
            <LucideIcon name="Trash2" className="w-3.5 h-3.5 mr-1 inline" />刪除
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-0">
      <StatsRow stats={displayStats} />

      <div className="px-4 py-4">
        <ProTableOt
          actionRef={actionRef}
          headerTitle={`${propsCategoryName}${propsCategoryTitle}`}
          columns={tableColumns}
          check={check}
          setCheck={setCheck}
          rowKey="id"
          toolBarRender={() => [
            <Button key="add" type="primary" onClick={openNew}>
              <LucideIcon name="Plus" className="w-4 h-4 mr-1 inline" />新增{propsCategoryName}
            </Button>,
          ]}
          extra_columns={[]}
          set_extra_columns={() => {}}
          manage_columns={false}
          request={handleRequest}
          rowSelection={false}
          propsWidth={900}
        />
      </div>

      <Modal
        open={modalOpen}
        title={editingRow ? `編輯${propsCategoryName}` : `新增${propsCategoryName}`}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="儲存"
        cancelText="取消"
        width={520}
      >
        <Form form={form} layout="vertical" className="mt-2">
          <Form.Item name="name" label="名稱" rules={[{ required: true, message: '請輸入名稱' }]}>
            <Input placeholder="請輸入名稱" />
          </Form.Item>
          <Form.Item name="category" label="分類" rules={[{ required: true, message: '請選擇分類' }]}>
            <Select options={CATEGORY_OPTIONS} placeholder="請選擇分類" />
          </Form.Item>
          <Form.Item name="status" label="狀態" rules={[{ required: true, message: '請選擇狀態' }]}>
            <Select options={STATUS_OPTIONS} placeholder="請選擇狀態" />
          </Form.Item>
          <Form.Item name="remark" label="說明">
            <Input placeholder="請輸入說明（選填）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Name12;
